import React, { useEffect, useMemo } from "react";
import "./Download.css";

const GOOGLE_PLAY_URL = "https://play.google.com/store/apps/details?id=com.acl.kitomobileapp";
const APP_STORE_URL = "https://apps.apple.com/app/kito/id6749103511";

function getLang() {
  const raw =
    localStorage.getItem("kitoLang") ||
    (navigator.language || "pt").slice(0, 2);

  return ["pt", "en", "es"].includes(raw) ? raw : "pt";
}

function getPlatform() {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera || "";

  if (/android/i.test(userAgent)) {
    return "android";
  }

  if (/iPad|iPhone|iPod/.test(userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)) {
    return "ios";
  }

  return "other";
}

const copyByLang = {
  pt: {
    eyebrow: "Link mágico Kito",
    title: "Estamos abrindo a loja certa para você",
    subtitle: "Se o redirecionamento não acontecer automaticamente, escolha sua loja abaixo.",
    redirecting: "Redirecionando para o download do Kito...",
    googlePlay: "Baixar no Google Play",
    appStore: "Baixar na App Store",
    desktopHint: "Em computadores ou navegadores não reconhecidos, mostramos as duas opções.",
    storyBadge: "Kito",
    storyText: "Histórias, imaginação e leitura em uma experiência acolhedora para famílias e crianças.",
    platformAndroid: "Android detectado",
    platformIos: "iPhone/iPad detectado",
    platformManual: "Escolha manual",
    readingPill: "Leitura infantil",
    safePill: "Experiência segura",
  },
  en: {
    eyebrow: "Kito smart link",
    title: "We are opening the right store for you",
    subtitle: "If the redirect does not happen automatically, choose your store below.",
    redirecting: "Redirecting to Kito download...",
    googlePlay: "Get it on Google Play",
    appStore: "Download on the App Store",
    desktopHint: "On desktop or unsupported browsers, both store options stay available.",
    storyBadge: "Kito",
    storyText: "Stories, imagination, and reading in a warm experience for families and children.",
    platformAndroid: "Android detected",
    platformIos: "iPhone/iPad detected",
    platformManual: "Manual choice",
    readingPill: "Children's reading",
    safePill: "Safe experience",
  },
  es: {
    eyebrow: "Enlace inteligente Kito",
    title: "Estamos abriendo la tienda correcta para ti",
    subtitle: "Si la redirección no ocurre automáticamente, elige tu tienda abajo.",
    redirecting: "Redirigiendo a la descarga de Kito...",
    googlePlay: "Descargar en Google Play",
    appStore: "Descargar en App Store",
    desktopHint: "En computadoras o navegadores no reconocidos, mostramos ambas opciones.",
    storyBadge: "Kito",
    storyText: "Historias, imaginación y lectura en una experiencia acogedora para familias y niños.",
    platformAndroid: "Android detectado",
    platformIos: "iPhone/iPad detectado",
    platformManual: "Elección manual",
    readingPill: "Lectura infantil",
    safePill: "Experiencia segura",
  },
};

export default function Download() {
  const lang = getLang();
  const t = copyByLang[lang];
  const platform = useMemo(() => getPlatform(), []);
  const isAutoRedirect = platform !== "other";

  useEffect(() => {
    const targetUrl =
      platform === "android" ? GOOGLE_PLAY_URL :
      platform === "ios" ? APP_STORE_URL :
      null;

    if (!targetUrl) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      window.location.replace(targetUrl);
    }, 150);

    return () => window.clearTimeout(timeoutId);
  }, [platform]);

  return (
    <main className="download-page">
      <section className="download-shell">
        <div className="download-showcase" aria-hidden="true">
          <div className="download-showcase-glow" />
          <img
            className="download-showcase-bg"
            src="/images/capa-loja.png"
            alt=""
          />
          <div className="download-showcase-overlay" />
          <div className="download-showcase-copy">
            <span className="download-story-badge">{t.storyBadge}</span>
            <p>{t.storyText}</p>
          </div>
          <div className="download-mascot-wrap">
            <span className="download-mascot-backdrop" />
            <img
              className="download-mascot"
              src="/images/kito.png"
              alt=""
            />
          </div>
        </div>

        <div className="download-card">
          <span className="download-eyebrow">{t.eyebrow}</span>
          <h1>{t.title}</h1>
          <p className="download-subtitle">{t.subtitle}</p>
          {isAutoRedirect && <p className="download-status">{t.redirecting}</p>}
          {!isAutoRedirect && <p className="download-status">{t.desktopHint}</p>}

          <div className="download-actions">
            <a
              className="download-button download-button-google"
              href={GOOGLE_PLAY_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t.googlePlay}
            </a>
            <a
              className="download-button download-button-apple"
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t.appStore}
            </a>
          </div>

          <div className="download-meta">
            <span className="download-meta-pill">
              {platform === "android" ? t.platformAndroid : platform === "ios" ? t.platformIos : t.platformManual}
            </span>
            <span className="download-meta-pill">{t.readingPill}</span>
            <span className="download-meta-pill">{t.safePill}</span>
          </div>
        </div>
      </section>
    </main>
  );
}
